import { Input } from "components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import { ChevronDown, Plus } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import DiscordIcon from "src/components/ui/icons/discord";

interface DiscordWebhook {
  id: string;
  name: string;
}

interface DiscordWebhookTypeaheadProps {
  webhooks: DiscordWebhook[];
  excludeWebhookIds?: string[];
  onSelect: (webhook: DiscordWebhook) => void;
  className?: string;
}

interface WebhookResultsListProps {
  webhooks: DiscordWebhook[];
  onSelect: (webhook: DiscordWebhook) => void;
  onAddWebhook: () => void;
}

const WebhookResultsList = React.memo<WebhookResultsListProps>(function WebhookResultsList({
  webhooks,
  onSelect,
  onAddWebhook
}) {
  return (
    <>
      {webhooks.length === 0 ? (
        <div className="py-4 text-center text-xs text-muted-foreground">
          No webhooks found
        </div>
      ) : (
        <div className="p-0.5">
          {webhooks.map((webhook) => (
            <button
              key={webhook.id}
              onClick={() => onSelect(webhook)}
              className="w-full flex items-center justify-between px-2 py-1.5 text-xs rounded hover:bg-card transition-colors text-left"
            >
              <div className="flex items-center gap-1.5 flex-1 min-w-0">
                <DiscordIcon className="h-4 w-4" />
                <span className="truncate text-foreground">{webhook.name}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Add Discord Webhook option */}
      <div className={`${webhooks.length > 0 ? "border-t border-border mt-1 pt-1" : ""} p-0.5`}>
        <button
          onClick={onAddWebhook}
          className="w-full flex items-center gap-1.5 px-2 py-1.5 text-xs rounded hover:bg-card transition-colors text-left"
        >
          <Plus className="h-3 w-3 text-primary shrink-0" />
          <span className="text-primary">Add Discord Webhook</span>
        </button>
      </div>
    </>
  );
});

export default function DiscordWebhookTypeahead({
  webhooks,
  excludeWebhookIds = [],
  onSelect,
  className = "",
}: DiscordWebhookTypeaheadProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [triggerWidth, setTriggerWidth] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredWebhooks = useMemo(() => {
    // Filter out excluded webhooks
    const availableWebhooks = webhooks.filter(
      (wh) => !excludeWebhookIds.includes(wh.id)
    );

    if (!searchQuery) {
      return availableWebhooks;
    }

    const query = searchQuery.toLowerCase();

    // Filter webhooks that contain the query
    return availableWebhooks.filter((wh) =>
      wh.name.toLowerCase().includes(query)
    );
  }, [webhooks, searchQuery, excludeWebhookIds]);

  useEffect(() => {
    if (triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, []);

  const handleSelect = (webhook: DiscordWebhook) => {
    onSelect(webhook);
    setOpen(false);
    setSearchQuery("");
    setIsTyping(false);
  };

  const handleAddWebhook = () => {
    // Open Integrations page in a new tab to avoid losing digest settings state
    setOpen(false);
    window.open("/settings/integrations?tab=connected&integration=discord&action=manage", "_blank");
  };

  const handleButtonClick = () => {
    if (!isTyping) {
      setIsTyping(true);
      setOpen(true);
      // Focus the input after a brief delay to ensure it's rendered
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  };

  // Handle popover close - always reset typing state and clear search
  useEffect(() => {
    if (!open) {
      setIsTyping(false);
      setSearchQuery("");
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <div
          ref={triggerRef as any}
          onClick={handleButtonClick}
          className={`flex h-9 w-full items-center justify-between rounded-md border border-border bg-transparent px-3 py-2 text-sm cursor-pointer hover:bg-card transition-colors ${className}`}
        >
          {isTyping ? (
            <>
              <Input
                ref={inputRef}
                placeholder="Search webhooks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-transparent border-0 h-auto p-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              />
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </>
          ) : (
            <>
              <span className="text-muted-foreground">Select a webhook...</span>
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 bg-popover border-border shadow-md"
        side="bottom"
        align="start"
        sideOffset={0}
        style={{ width: triggerWidth > 0 ? `${triggerWidth}px` : "100%" }}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div
          className="max-h-[300px] overflow-y-scroll scrollbar-thin scrollbar-thumb-neutral-600 scrollbar-track-neutral-800"
          onWheel={(e) => {
            // Handle scroll manually
            const container = e.currentTarget;
            const delta = e.deltaY;
            container.scrollTop += delta;
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <WebhookResultsList
            webhooks={filteredWebhooks}
            onSelect={handleSelect}
            onAddWebhook={handleAddWebhook}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
